/**
 * Google OAuth 2.0 — Google Sheets / Drive
 *
 * The browser only performs the authorize redirect; the code exchange and the
 * token refresh go through kio-api, which holds the OAuth client secret.
 *
 * Env vars required:
 *   REACT_APP_SHEET_CLIENT_ID
 *   REACT_APP_SHEET_REDIRECT_URI
 */

import { apiFetch } from './api';

const CLIENT_ID     = process.env.REACT_APP_SHEET_CLIENT_ID;
const REDIRECT_URI  = process.env.REACT_APP_SHEET_REDIRECT_URI;

// drive.file only: KIO reads and writes the spreadsheets it creates. Google's `spreadsheets` scope (every sheet of
// the seller) is "sensitive": without Google's verification of the app, the consent screen warned sellers that
// "Google n'a pas validé cette application" (Walid 29/10).
const SCOPES = 'https://www.googleapis.com/auth/drive.file';

const LS_TOKEN         = 'sheetToken';
const LS_REFRESH_TOKEN = 'sheetRefreshToken';
const LS_OAUTH_STATE   = 'sheetOAuthState';

// ── Token storage ─────────────────────────────────────────────────────────────

export function getSheetAccessToken() {
  return localStorage.getItem(LS_TOKEN) || null;
}

export function getSheetRefreshToken() {
  return localStorage.getItem(LS_REFRESH_TOKEN) || null;
}

export function hasSheetRefreshToken() {
  return Boolean(getSheetRefreshToken());
}

export function storeSheetTokens({ access_token, refresh_token }) {
  if (access_token)  localStorage.setItem(LS_TOKEN, access_token);
  if (refresh_token) localStorage.setItem(LS_REFRESH_TOKEN, refresh_token);
}

export function clearSheetTokens() {
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_REFRESH_TOKEN);
  localStorage.removeItem(LS_OAUTH_STATE);
}

// ── OAuth redirect ────────────────────────────────────────────────────────────

export function getGoogleSheetAuth() {
  const state = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  localStorage.setItem(LS_OAUTH_STATE, state);

  const params = new URLSearchParams({
    client_id:     CLIENT_ID,
    redirect_uri:  REDIRECT_URI,
    response_type: 'code',
    scope:         SCOPES,
    access_type:   'offline',
    prompt:        'consent',
    state,
  });

  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

// ── kio-api token routes ──────────────────────────────────────────────────────

async function postTokenRoute(storeSlug, route, body) {
  if (!storeSlug) throw new Error('Boutique introuvable');
  const res = await apiFetch(`/store/${storeSlug}/sheet/${route}?format=json`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.detail || data.error || `Google token error ${res.status}`);
    err.code = data.error;
    err.status = res.status;
    throw err;
  }
  return data;
}

// ── Code → tokens ─────────────────────────────────────────────────────────────

export async function exchangeCodeForTokens(storeSlug, code) {
  const data = await postTokenRoute(storeSlug, 'token/', { code, redirect_uri: REDIRECT_URI });
  storeSheetTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
  return data;
}

// ── Token refresh ─────────────────────────────────────────────────────────────

export async function ensureSheetAccessToken(storeSlug) {
  const refreshToken = getSheetRefreshToken();
  if (!refreshToken) {
    clearSheetTokens();
    throw new Error('reauth_required');
  }

  const accessToken = getSheetAccessToken();
  if (accessToken) return accessToken;

  return getNewToken(storeSlug);
}

export async function getNewToken(storeSlug) {
  const refreshToken = getSheetRefreshToken();
  if (!refreshToken) throw new Error('reauth_required');

  let data;
  try {
    data = await postTokenRoute(storeSlug, 'token/refresh/', { refresh_token: refreshToken });
  } catch (e) {
    if (e.code === 'invalid_grant') {
      clearSheetTokens();
      throw new Error('reauth_required');
    }
    throw e;
  }

  localStorage.setItem(LS_TOKEN, data.access_token);
  return data.access_token;
}

// ── Validate OAuth callback state ─────────────────────────────────────────────

export function validateOAuthState(returnedState) {
  const saved = localStorage.getItem(LS_OAUTH_STATE);
  localStorage.removeItem(LS_OAUTH_STATE);
  return saved && saved === returnedState;
}
